<script setup lang="ts">
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import { useDatabaseStore } from '@/stores/databaseStore'
import { useEstimateStore } from '@/stores/estimateStore'
import type { AssemblyVariableValue } from '@/types/customTypes'
import { computed, ref, toRaw, watch } from 'vue'
import type { DataTableColumn } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'
import DatabaseAssemblyFormulaEditor from './DatabaseAssemblyFormulaEditor.vue'

const props = defineProps<{ assemblyId: string }>()
const testVariables = defineModel<Record<string, AssemblyVariableValue>>('testVariables')

const databaseStore = useDatabaseStore()

const assemblyItems = computed(
  () => databaseStore.assemblyItems?.filter((a) => a.assembly_id == props.assemblyId) ?? [],
)
const testQuantities = ref<{ assemblyItemId: string; value: string | number | undefined }[]>([])

const rows = computed(() => {
  return (
    assemblyItems.value?.map((assemblyItem) => {
      const item = databaseStore.items?.find((i) => i.id == assemblyItem.item_id)
      const subdivision = databaseStore.itemDivisions?.find((s) => s.id == item?.item_division_id)
      const division = databaseStore.itemDivisions?.find(
        (d) => d.id == subdivision?.parent_division_id,
      )
      return {
        id: assemblyItem.id,
        code: `${division?.code}.${subdivision?.code}.${item?.code}`,
        name: item?.name,
        quantity: testQuantities.value.find((tq) => tq.assemblyItemId === assemblyItem.id)?.value,
        unit_of_measure: databaseStore.unitsOfMeasure?.find((uom) => uom.id === item?.uom_id)?.code,
        formula: assemblyItem.formula,
        item: item,
      }
    }) ?? []
  )
})
type Row = (typeof rows.value)[number]
const columns = ref<DataTableColumn<Row>[]>([
  {
    columnDataKey: 'code',
    name: 'Code',
    columnType: 'disabled',
    defaultWidth: 125,
  },
  {
    columnDataKey: 'name',
    name: 'Name',
    columnType: 'disabled',
    defaultWidth: 200,
  },
  {
    columnDataKey: 'unit_of_measure',
    name: 'Unit',
    columnType: 'disabled',
    defaultWidth: 75,
  },
  {
    columnDataKey: 'formula',
    name: 'Formula',
    defaultWidth: 200,
    columnType: 'disabled',
  },
  {
    columnDataKey: 'quantity',
    name: 'Qty',
    columnType: 'disabled',
    defaultWidth: 75,
  },
])

const estimateStore = useEstimateStore()
async function testFormula(formula: string) {
  return estimateStore.calculateItemQtyFromAssemblyFormula(formula, toRaw(testVariables.value))
}

watch(
  testVariables,
  async () => {
    const newTestQuantities = []
    for (const assemblyItem of assemblyItems.value) {
      const result = assemblyItem.formula
        ? await testFormula(toRaw(assemblyItem.formula))
        : undefined
      newTestQuantities.push({ assemblyItemId: assemblyItem.id, value: result })
    }
    testQuantities.value = newTestQuantities
  },
  { deep: true },
)

async function editAssemblyItem(row: Row) {
  const { data, error } = await supabase
    .from('assembly_item')
    .update({
      formula: row.formula,
    })
    .eq('id', row.id)
    .select()
    .single()
  if (error) throw toastThrowError('Unable to update assembly item', error)
  const index = databaseStore.assemblyItems?.findIndex((a) => a.id == row.id)
  if (index == undefined) throw toastThrowError('Unable to locate index for assembly item')
  databaseStore.assemblyItems?.splice(index, 1, data)
}

async function deleteAssemblyItem(row: Row) {
  const result = await supabase.from('assembly_item').delete().eq('id', row.id)
  if (result.error) throw toastThrowError('Unable to delete assembly item', result.error)
  const index = databaseStore.assemblyItems?.findIndex((a) => a.id == row.id)
  if (index == undefined) throw toastThrowError('Unable to locate index for assembly item')
  databaseStore.assemblyItems?.splice(index, 1)
}

const editingAssemblyItemId = ref<string>()
</script>

<template>
  <div class="flex h-full w-full">
    <!-- Details grid -->
    <BaseDataTable
      table-name="DBAssemblyEditDetails"
      :columns="columns"
      :rows="rows"
      :row-key="'id'"
      @row-update="editAssemblyItem"
      show-row-actions
    >
      <template #rowActions="props">
        <div class="flex w-max justify-center gap-1">
          <button
            class="cursor-pointer rounded p-1 hover:bg-gray-200"
            title="Open formula editor"
            @click="editingAssemblyItemId = props.row.id"
          >
            <svg
              width="24px"
              height="24px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 12.75V11.25H9.38563L10.7931 6.00266C10.797 5.98811 10.8013 5.97377 10.806 5.95965L10.8102 5.94389L10.8146 5.92986C11.1634 4.81245 12.2058 4 13.44 4C14.9588 4 16.19 5.23122 16.19 6.75C16.19 6.91899 16.1747 7.08506 16.1451 7.24669L16.1396 7.2767L15.8408 8.39079L14.392 8.0022L14.6741 6.9505C14.6845 6.88564 14.69 6.81871 14.69 6.75C14.69 6.05964 14.1304 5.5 13.44 5.5C12.8852 5.5 12.413 5.86199 12.2504 6.36445L10.9394 11.25H12V12.75H10.5368L9.48551 16.6678L9.48495 16.6699L9.41212 16.9414C9.4089 16.9535 9.4054 16.9653 9.40164 16.977L9.38363 17.0435L9.38013 17.0549C9.03607 18.1802 7.98975 19 6.75 19C5.23122 19 4 17.7688 4 16.25C4 15.8992 4.08642 15.5956 4.14978 15.373L4.16503 15.3192L4.30629 14.7925L4.38192 14.5349L5.8212 14.9573L5.75052 15.1981L5.6104 15.7206C5.53143 16.0018 5.5 16.1153 5.5 16.25C5.5 16.9404 6.05964 17.5 6.75 17.5C7.30821 17.5 7.78276 17.1335 7.94256 16.6264L8.03671 16.2792L8.03726 16.2772L8.98331 12.75H7Z"
                fill="#1F2328"
              />
              <path
                d="M20.0002 17.75L18.9396 18.8107L16.9699 16.841L15.0002 18.8107L13.9396 17.75L15.9093 15.7803L13.9396 13.8107L15.0002 12.75L16.9699 14.7197L18.9396 12.75L20.0002 13.8107L18.0306 15.7803L20.0002 17.75Z"
                fill="#1F2328"
              />
            </svg>
          </button>
          <button
            class="cursor-pointer rounded p-1 hover:bg-gray-200"
            @click="deleteAssemblyItem(props.row)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </button>
        </div>
      </template>
    </BaseDataTable>
    <DatabaseAssemblyFormulaEditor
      v-if="editingAssemblyItemId"
      :assembly-item-id="editingAssemblyItemId"
      @close="editingAssemblyItemId = undefined"
    />
  </div>
</template>
